import { AfterViewInit, Component, ElementRef, NgZone, OnDestroy, OnInit, signal, ViewChild, ViewEncapsulation, WritableSignal } from '@angular/core';
import { UntypedFormBuilder, UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { ToastrService } from 'ngx-toastr';
import { CountryModel } from 'src/app/model/country.model';
import { ApiService } from 'src/app/services/api.service';
import { environment } from 'src/environments/environment';
import { HearAboutUsModel } from '../../../model/hear-about-us.model';
import { OauthKickOffModel } from '../../../model/oauth/oauth-kick-off.model';
import { SignupReqModel } from '../../../model/register/signup-req.model';
import { SignupSsoInfoModel } from '../../../model/register/signup-sso-info.model';
import { AuthService } from '../../../services/auth.service';
import { CaptchaService } from '../../../services/captcha.service';
import { IntegrationService, OauthConnectionType } from '../../../services/integration.service';
import { alphaNumericRegex, ValidateLength } from '../../shared/common-validator';
import { SharedService } from '../../shared/shared.service';
import { SPIN_APPEND, SPIN_STYLES, SpinInputModel } from 'countable@model';
import { finalize } from 'rxjs';

@Component({
  selector: 'app-signup',
  templateUrl: './signup.component.html',
  styleUrls: ['./signup.component.scss'],
  encapsulation: ViewEncapsulation.None,
  standalone: false
})
export class SignupComponent implements OnInit, AfterViewInit, OnDestroy {

  @ViewChild('googleSSO') googleSSODiv: ElementRef<HTMLElement>;
  @ViewChild('ssoExistingFirm') ssoExistingFirm: ElementRef<HTMLElement>;

  signupFrom: UntypedFormGroup;
  isRegistrationFinished: boolean = false;
  imagePath = 'bg-0.jpg';
  referenceData: HearAboutUsModel[] = [];
  subReferenceData: HearAboutUsModel[] = [];
  nextSubReferenceData: HearAboutUsModel[] = [];
  cameFromReferral: boolean = false;
  countries: CountryModel[] = [];
  isSsoSignupInProgress: boolean = false;
  isSsoFirmExist: boolean = false;
  isSsoSignup: boolean = false;
  ssoProvider: string;
  referenceName1: string;
  referenceName2: string;
  imgCollection: Array<object> = [
    {
      thumbImage: '/assets/images/login-screen-images/bg-0.jpg',
      alt: 'Image 1'
    }, {
      thumbImage: '/assets/images/login-screen-images/bg-1.jpg',
      alt: 'Image 2'
    }
  ];
  private readonly clearBgTime: any;
  public spinnerConfig: WritableSignal<SpinInputModel> = signal<SpinInputModel>(new SpinInputModel(SPIN_APPEND.BODY,SPIN_STYLES.BLUR_WITH_SPIN,false)); 
  private spinCount: number = 0; 

  constructor(private captchaService: CaptchaService, private authService: AuthService, private apiService: ApiService,
              private shareService: SharedService, private fb: UntypedFormBuilder, private toaster: ToastrService,
              private router: Router, private route: ActivatedRoute, private ngZone: NgZone, private integrationService: IntegrationService) {
    this.route.queryParamMap.subscribe(params => {
      let msg = '';
      if (params.has('error')) {
        if (params.get('error') == 'locked_account') {
          msg = 'Account is locked';
        }
        if (params.get('error') == 'existing_account') {
          msg = 'Account exists, please try to login';
        }
        if (params.get('error') == 'access_denied') {
          msg = 'Access Denied';
        }
        if (params.get('error') == 'invalid_request') {
          msg = 'Invalid request, please try again';
        }
        this.router.navigate(['./signup']).then(() => {
          this.toaster.error(msg);
        });
      }
      if (params.has('code')) {
        this.isSsoSignupInProgress = true; // we just put the window in wait until captcha is loaded
        setTimeout(() => {this.prepareSignupOnSsoCallback(params.get('code'));}, 1000);
      }
    });
    this.route.params.subscribe(params => {
      if (params.type) {
        this.isSsoSignupInProgress = true;
        setTimeout(() => {
          this.signupWithOAuthKickOff(params.type.toLowerCase());
        }, 1000);
      }
    });
    this.clearBgTime = setInterval(() => {
      this.imagePath = 'bg-' + Math.floor(Math.random() * 2) + '.jpg';
    }, 5000);
  }

  ngAfterViewInit(): void {
    this.captchaService.showCaptchaBanner();
    this.loadGoogle();
  }

  ngOnDestroy() {
    this.captchaService.hideCaptchaBanner();
    clearInterval(this.clearBgTime);
  }

  ngOnInit() {
    this.authService.cleanTheStorage();
    this.getCountries();
    this.signupFrom = this.fb.group({
      'country': new UntypedFormControl('', [Validators.required]),
      'firstname': new UntypedFormControl('', [Validators.required, ValidateLength]),
      'lastname': new UntypedFormControl('', [Validators.required, ValidateLength]),
      'firm': new UntypedFormControl('', [Validators.required]),
      'firmSize': new UntypedFormControl('', [Validators.required]),
      'email': new UntypedFormControl('', {validators: [Validators.required, Validators.pattern(environment.EMAIL_REG_EXP)], updateOn: 'blur'}),
      'phone': new UntypedFormControl('', [Validators.minLength(14), Validators.maxLength(14)]),
      'accept': new UntypedFormControl('', [Validators.required]),
      'referenceId1': new UntypedFormControl('', [Validators.required]),
      'referenceId2': new UntypedFormControl('', [Validators.required]),
      'referenceId3': new UntypedFormControl(''),
      'reference': new UntypedFormControl('')
    });
    this.getReferenceDropdown();
    this.signupFrom.get('referenceId1').valueChanges.subscribe(() => {
      this.updateSelectedName('referenceId1');
    });
    this.signupFrom.get('referenceId2').valueChanges.subscribe(() => {
      this.updateSelectedName('referenceId2');
    });
  }

  signupWithOAuthKickOff(type: string) {
    this.isSsoSignupInProgress = true;
    try {
      this.captchaService.getCaptchaToken('sso_signup_' + type).then((captcha) => {
        const data: OauthKickOffModel = {
          redirectMode: OauthConnectionType.SIGNUP, // enum in IntegrationOauthStateEntity
          key: type,
          captcha: captcha
        };
        this.integrationService.oauthProvidersKickOff(data);
      });
    } catch (e) {
      this.isSsoSignupInProgress = false;
      this.toaster.error('Could not initiate the sso signup process');
    } finally {
      this.loadGoogle();
    }
  }

  prepareSignupOnSsoCallback(code: string) {
    try {
      this.captchaService.getCaptchaToken('sso_signup_oauth').then((captcha) => {
        this.authService.prepareSignupWithSso(code, captcha).subscribe({
          next: res => {
            if (res.status === 200) {
              if (res.data.isExistingUser) {
                this.toaster.error('Account exists with this email address, please login');
              } else {
                this.setFieldsAfterSsoPrepare(res.data);
              }
            }
            this.router.navigate(['./signup']).then(() => {
              res.errors.forEach((error) => {
                this.toaster.error(error);
              });
            });
          }, error: () => {
            this.toaster.error('Could not verify the SSO credentials, please try again');
          }
        }).add(() => {
          this.isSsoSignupInProgress = false;
          this.loadGoogle();
        });
      });
    } catch (e) {
      this.isSsoSignupInProgress = false;
      this.toaster.error('Could not verify the SSO credentials, please try again');
    }
  }

  prepareSignupOnGoogleCallback(token) {
    this.captchaService.getCaptchaToken('sso_signup_google').then((captcha) => {
      this.isSsoSignupInProgress = true;
      this.authService.prepareSignupWithGoogle({token: token, captcha: captcha}).subscribe({
        next: res => {
          if (res.status === 200) {
            if (res.data.isExistingUser) {
              this.toaster.warning('Email address already present, please use another one');
            } else {
              this.setFieldsAfterSsoPrepare(res.data);
            }
          } else {
            res.errors.forEach((error) => {
              this.toaster.error(error);
            });
          }
        }, error: () => {
          this.toaster.error('Authentication failed');
        }
      }).add(() => {
        this.isSsoSignupInProgress = false;
      });
    });
  }

  signup() {
    if (!this.signupFrom.valid) {
      return;
    }
    this.captchaService.getCaptchaToken('registration').then(token => {
      this.isSsoSignupInProgress = true;
      const formData = this.signupFrom.getRawValue();
      const data: SignupReqModel = {
        isSsoEnabled: this.isSsoSignup,
        country: formData.country,
        firm: formData.firm.trim(),
        firstname: formData.firstname.trim(),
        lastname: formData.lastname.trim(),
        firmSize: formData.firmSize,
        email: formData.email,
        phone: formData.phone,
        heardAboutUsId: formData.referenceId3 ? formData.referenceId3 : formData.referenceId2 ? formData.referenceId2 :formData.referenceId1,
        comment: formData.reference.trim(),
        anonymousKey: token
      };
      this.updateSpinnerConfig(true);
      this.authService.signup(data).pipe(finalize(() => this.updateSpinnerConfig(false))).subscribe({
        next: res => {
          if (res.status === 200) {
            this.isRegistrationFinished = true;
            if (this.isSsoSignup) {
              this.router.navigate(['./login', this.ssoProvider]).then(() => {
                this.toaster.success('SSO Signup finished, initiating the login process ...');
              });
            }
          } else {
            this.signupFrom.setErrors({'incorrect': true});
            this.toaster.error(res.errors[0]);
          }
        },
        error: error => {
          this.isSsoSignupInProgress = false;
          this.signupFrom.setErrors({'incorrect': true});
          if (error.status === 400) {
            error.error.fieldErrors.forEach(field => {
              this.toaster.error('Please correct the field(s) on the registration form and submit again');
              if (field.field === 'email') {
                this.signupFrom.controls['email'].setErrors({'incorrect': true});
              }
              if (field.field === 'firm') {
                this.signupFrom.controls['firm'].setErrors({'incorrect': true});
              }
              if (field.field === 'firstname') {
                this.signupFrom.controls['firstname'].setErrors({'incorrect': true});
              }
              if (field.field === 'lastname') {
                this.signupFrom.controls['lastname'].setErrors({'incorrect': true});
              }
              if (field.field === 'country') {
                this.signupFrom.controls['country'].markAsTouched();
                this.signupFrom.controls['country'].setErrors({'incorrect': true});
              }
            });
          }
        }
      }).add(() => {
        this.isSsoSignupInProgress = false;
      });
    });
  }

  disableInputs(fieldName) {
    this.signupFrom.controls[fieldName].disable();
  }

  enableInputs(fieldName) {
    if (this.signupFrom.controls[fieldName].disabled) {
      this.signupFrom.controls[fieldName].enable();
    }
    switch (fieldName) {
      case 'referenceId2':
        const selectedId1 = this.signupFrom.get('referenceId1').value;
        const selectedReference1 = this.referenceData.find(item => item.hash === selectedId1);
        this.subReferenceData = selectedReference1 ? selectedReference1.children : [];
        this.subReferenceData = this.subReferenceData.map(item => ({
          ...item,
          name: item.name.length > 40 ? item.name.substr(0, 40) + '...' : item.name
        }));
        // Update validators for referenceId2
        if (this.subReferenceData.length > 0) {
          this.signupFrom.get('referenceId2').setValidators([Validators.required]);
        } else {
          this.signupFrom.get('referenceId2').clearValidators();
        }
        this.signupFrom.get('referenceId2').updateValueAndValidity();
        // Reset the next dropdowns
        this.signupFrom.get('referenceId2').setValue('');
        this.nextSubReferenceData = [];
        if (this.signupFrom.get('referenceId1').value === 'a8b34cca-2fc4-1') {
          this.signupFrom.get('reference').setValidators(Validators.required);
        } else {
          this.signupFrom.get('reference').clearValidators();
          this.signupFrom.get('reference').setValue('');
        }
        break;
      case 'referenceId3':
        const selectedId2 = this.signupFrom.get('referenceId2').value;
        const selectedReference2 = this.subReferenceData.find(item => item.hash === selectedId2);
        this.nextSubReferenceData = selectedReference2 ? selectedReference2.children : [];
        // Update validators for referenceId3
        if (this.nextSubReferenceData.length > 0) {
          this.signupFrom.get('referenceId3').setValidators([Validators.required]);
        } else {
          this.signupFrom.get('referenceId3').clearValidators();
        }
        // Reset the next dropdown
        this.signupFrom.get('referenceId3').setValue('');

        this.signupFrom.get('referenceId3').updateValueAndValidity();
        if (this.signupFrom.get('referenceId2').value === 'e99815fc-2fd' || this.signupFrom.get('referenceId2').value === '1777e57d-2fd') {
          this.signupFrom.get('reference').setValidators(Validators.required);
        } else {
          this.signupFrom.get('reference').clearValidators();
          this.signupFrom.get('reference').setValue('');
        }

        break;
    }
  }

  setValue(fieldName, value?) {
    this.signupFrom.controls[fieldName].setValue(value ? value : null);
  }

  getReferenceDropdown() {
    this.apiService.getHeardAboutUsList().subscribe({
      next: res => {
        if (res.status === 200) {
          this.referenceData = res.data;
          this.enableInputs('referenceId1');
        }
      },
      error: err => {
        this.toaster.error(err);
      }
    });
  }

  alphaNumeric(event, type: 'firm') {
    const alphaNumeric = event.target.value.replace(alphaNumericRegex, '');
    if (type === 'firm') {
      this.signupFrom.controls[type].setValue(alphaNumeric);
    }
  }

  public onlyChars(event, type) {
    const noSpecialChar = event.target.value.replace(/[^a-zA-Z ]/g, '');
    if (type == 'fn') {
      this.signupFrom.controls['firstname'].setValue(noSpecialChar);
    } else if (type == 'ln') {
      this.signupFrom.controls['lastname'].setValue(noSpecialChar);
    } else if (type == 'firm') {
      this.signupFrom.controls['firm'].setValue(noSpecialChar);
    }
  }

  public onlyNumeric(event, type) {
    const onlyNums = event.target.value.replace(/[^0-9]/g, '');
    this.signupFrom.controls['firmSize'].setValue(onlyNums);
  }

  public handlePhone(event, type) {
    if (type !== 'cell') {
      return;
    }
    const onlyNums = event.target.value.replace(/[^0-9]/g, '');
    if (onlyNums.length < 10) {
      this.signupFrom.controls['phone'].setValue(onlyNums);
    } else if (onlyNums.length === 10) {
      this.signupFrom.controls['phone'].setValue(onlyNums.replace(/(\d{3})(\d{3})(\d{4})/, '($1) $2-$3'));
    }
  }

  openTermsPrivacyDialog(val) {
    this.shareService.termsPrivacyShow.next(val);
  }

  siteNavigate() {
    window.open('https://countable.co/', '_self');
  }

  updateSelectedName(controlName: string): void {
    const selectedId = this.signupFrom.get(controlName).value;
    let selectedItem: HearAboutUsModel;
    if (controlName === 'referenceId1') {
      selectedItem = this.referenceData.find(item => item.hash === selectedId);
      this.referenceName1 = selectedItem ? selectedItem.name : '';
    } else if (controlName === 'referenceId2') {
      selectedItem = this.subReferenceData.find(item => item.hash === selectedId);
      this.referenceName2 = selectedItem ? selectedItem.name : '';
    }
  }

  private loadGoogle() {
    if (this.isSsoSignupInProgress) {
      return;
    }
    if (typeof google == 'undefined' || !this.googleSSODiv || !this.googleSSODiv.nativeElement) {
      setTimeout(() => {
        this.loadGoogle();
      }, 100);
      return;
    }
    // @ts-ignore
    google.accounts.id.initialize({
      client_id: environment.GOOGLE_CLIENT_ID,
      callback: ({credential}) => {
        this.ngZone.run(() => {
          this.prepareSignupOnGoogleCallback(credential);
        });
      }
    });
    // @ts-ignore
    google.accounts.id.renderButton(
      this.googleSSODiv.nativeElement,
      {
        theme: 'outline',
        size: 'large',
        type: 'label',
        shape: 'rectangular',
        text: 'Sign in with Google',
        logo_alignment: 'center'
      }
    );
    if (!this.isSsoSignup) {
      // @ts-ignore
      google.accounts.id.prompt(); // also display the One Tap dialog
    }
  }

  private setFieldsAfterSsoPrepare(data: SignupSsoInfoModel) {
    this.isSsoSignup = true;
    this.ssoProvider = data.provider;
    this.signupFrom.get('email').setValue(data.email);
    this.disableInputs('email');
    if (data.firstname) {
      this.signupFrom.get('firstname').setValue(data.firstname);
    }
    if (data.lastname) {
      this.signupFrom.get('lastname').setValue(data.lastname);
    }
    if (data.firm) {
      this.signupFrom.get('firm').setValue(data.firm);
    }
    if (data.country) {
      this.signupFrom.get('country').setValue(this.countries.filter(country => {return country.iso === data.country;}).shift().id);
    }
    if (data.phone) {
      data.phone = data.phone.startsWith('+1') ? data.phone.substring(2) : data.phone;
      this.signupFrom.controls['phone'].setValue(data.phone.replace(/(\d{3})(\d{3})(\d{4})/, '($1) $2-$3'));
    }
    this.signupFrom.markAllAsTouched();
  }

  private getCountries() {
    this.apiService.getCountries(1).subscribe(res => {
      if (res) {
        this.countries = res;
      }
    });
  }

  private updateSpinnerConfig(value: boolean) { 
      value ? this.spinCount++ : (this.spinCount = Math.max(0, this.spinCount - 1)); 
      const spinner = this.spinnerConfig(); 
      this.spinnerConfig.set({ 
       ...spinner, 
         isSpinShown: this.spinCount > 0
       }); 
  
     }
}
